<script lang="ts">
  import type { MissionInfo } from '../app';
  import { type TimelineView, timeToX } from '../core/timelineScale';
  import { lang } from '../i18n';
  import TypeIcon from './TypeIcon.svelte';

  // One mission on the timeline (S3-15, D14): a bar from launch to the end of the mission; the silent part (contact
  // lost, the spacecraft flies on) and the predicted part (after today) are hatched, not only lighter, so colour is
  // never the only carrier of the difference. Missions share lanes (core/timelineLanes.ts), so the bar is a button
  // only as wide as the bar and its label. A tap selects the mission.
  let {
    mission,
    view,
    now,
    max,
    selected,
    onselect,
    name,
    labelWidth,
  }: {
    mission: MissionInfo;
    view: TimelineView;
    /** The real "now" (UTC ms). */
    now: number;
    /** End of the time range (UTC ms). */
    max: number;
    selected: boolean;
    onselect: (id: string) => void;
    /** Accessible name, e.g. "Cassini–Huygens, 1997–2017". */
    name: string;
    /** Estimated width of the visible label, px (the lane packing reserves it too). */
    labelWidth: number;
  } = $props();

  interface Part {
    kind: 'solid' | 'silent' | 'predicted' | 'dot';
    x: number;
    w: number;
  }

  const parts = $derived.by((): Part[] => {
    const m = mission;
    const solidEnd = Math.min(m.end ?? Infinity, now);
    const out: Part[] = [];
    const add = (kind: Part['kind'], a: number, b: number): void => {
      if (!(b > a)) return;
      // clipped to a little outside the track, so huge spans never produce huge elements
      const x0 = Math.max(-20, timeToX(view, a));
      const x1 = Math.min(view.width + 20, timeToX(view, b));
      if (x1 - x0 > 0) out.push({ kind, x: x0, w: Math.max(x1 - x0, 2) });
    };
    add('solid', m.launch, solidEnd);
    const trajEnd = Math.min(m.trajectoryEnd ?? -Infinity, max);
    if (m.end !== null && trajEnd > m.end) add('silent', m.end, trajEnd);
    if (m.end === null) add('predicted', now, Math.max(trajEnd, now));
    // missions of a few days (Vostok 1: 108 minutes) are a dot at this zoom (S3-21)
    const total = out.reduce((a, p) => a + p.w, 0);
    if (total < 6) {
      const x = timeToX(view, m.launch);
      return x > -20 && x < view.width + 20 ? [{ kind: 'dot', x: x - 4, w: 8 }] : [];
    }
    return out;
  });

  /** Lane coordinates (px): the bar, the label and the button around both. */
  const box = $derived.by(() => {
    if (!parts.length) return null;
    const start = parts[0]!.x;
    const end = parts[parts.length - 1]!.x + parts[parts.length - 1]!.w;
    const dot = parts[0]!.kind === 'dot';
    // the name starts at the bar (right of a dot); a bar that began off-screen keeps it inside its own span, so it
    // never covers a neighbour in the same lane
    const labelX = dot ? start + 12 : start >= 4 ? start : Math.min(4, end - labelWidth);
    const left = Math.min(start, labelX);
    const right = Math.max(end, labelX + labelWidth);
    return { left, width: right - left, labelX: labelX - left };
  });
</script>

<button
  type="button"
  class="bar-button"
  class:selected
  class:offscreen={!box}
  aria-label={name}
  aria-pressed={selected}
  data-mission={mission.id}
  style:--bar-color={mission.color}
  style:transform={box ? `translateX(${box.left}px)` : undefined}
  style:width={box ? `${box.width}px` : undefined}
  onclick={() => onselect(mission.id)}
>
  {#if box}
    {#each parts as p (p.kind)}
      <span class="bar {p.kind}" style:transform="translateX({p.x - box.left}px)" style:width="{p.w}px"></span>
    {/each}
    <span class="name" style:transform="translateX({box.labelX}px)"
      ><TypeIcon type={mission.type} size={11} />
      {mission.data.name[$lang]}</span
    >
  {/if}
</button>

<style>
  .bar-button {
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    padding: 0;
    text-align: left;
  }
  /* 44 px touch targets (lanes are 26 px tall): the hit area reaches into the neighbours' gaps */
  .bar-button::after {
    content: '';
    position: absolute;
    inset: -9px 0;
  }
  /* missions outside the visible span stay in the tab order (and for screen readers), without a box */
  .bar-button.offscreen {
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip-path: inset(50%);
  }
  .bar {
    position: absolute;
    left: 0;
    top: 9px;
    height: 10px;
    background: var(--bar-color);
    opacity: 0.75;
  }
  .bar-button.selected .bar {
    opacity: 1;
    top: 7px;
    height: 14px;
  }
  /* hatched, not only lighter: silent and predicted parts read without colour (AGENTS.md) */
  .bar.silent {
    background: repeating-linear-gradient(135deg, var(--bar-color) 0 3px, transparent 3px 7px);
  }
  .bar.dot {
    top: 9px;
    height: 8px;
    border-radius: 50%;
  }
  .bar-button.selected .bar.dot {
    top: 8px;
    height: 10px;
  }
  .bar.predicted {
    background: repeating-linear-gradient(90deg, var(--bar-color) 0 6px, transparent 6px 10px);
  }
  .name {
    display: flex;
    align-items: center;
    gap: 5px;
    position: absolute;
    left: 0;
    top: 4px;
    padding: 1px 6px;
    background: var(--scrim);
    color: var(--fg);
    font-size: 0.7rem;
    font-weight: 600;
    letter-spacing: var(--track-caps);
    text-transform: uppercase;
    white-space: nowrap;
    pointer-events: none;
  }
  .bar-button.selected .name,
  .bar-button:hover .name {
    color: var(--accent);
  }
  .bar-button:focus-visible {
    outline: 2px solid var(--focus);
    outline-offset: -2px;
  }
</style>
